/** Options to configure LIQA‘s look and behavior. */
export type Props = {
  /** The license key obtained from {@link https://haut.ai/#contacts Haut.ai }.  */
  license: string
  /** A string containing valid CSS rules. */
  styles?: string
  /** An object whose keys are translation keys and values are translation texts. */
  messages?: Messages
  /**
   * A preset used to customize AI quality criteria and user flow.
   * @default "face"
   */
  preset?: "face" | "face-180" | "hair"
  /**
   * Overrides for the default preset‘s quality settings.
   * @hidden
   */
  settings?: Partial<{
    lighting: number
  }>
  /**
   * A list of sources to capture the image from.
   *
   * If "front_camera" is specified, the front camera will be used to capture the image.
   * If the device has no front camera but has a back camera - the back camera will be used as a fallback.
   *
   * If "back_camera" is specified, the back camera will be used to capture the image.
   * If the device has no back camera but has a front camera - the front camera will be used as a fallback.
   *
   * If "camera" is specified - acts as an alias for "front_camera".
   *
   * If more than one camera is specified as a source, the first one will be used as the initial one.
   *
   * If only one "upload" source is specified, the file upload will be used to capture the image.
   * @default ["camera"]
   */
  sources?:
    | ["camera" | "front_camera" | "back_camera"]
    | ["upload"]
    | ["camera" | "front_camera" | "back_camera", "upload"]
  /**
   * Image capture mode for `"camera"` source.
   * `"auto"` means LIQA will automatically take a photo when all AI quality criteria met.
   * `"manual"` means LIQA will allow the user to take a photo on their own via a button.
   * The option is only applicable to "hair" preset. For other presets it is always "auto".
   */
  capture?: "auto" | "manual"
}

export type Messages = {
  [key: string]: string | Messages
}

/** Preset to be used if no `preset` option is specified to LIQA */
export const DEFAULT_PRESET = "face"

export const oldSourcesToNewSources = <T extends string[]>(sources: T) => {
  if (sources.includes("photo")) {
    console.warn(
      `[LIQA] Deprecation warning: the "photo" option of the "sources" API has been deprecated. Use "upload" instead.`,
    )
    return sources.map((source) => (source === "photo" ? "upload" : source)) as T
  }
  return sources
}
